import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"
import { useId } from "react"
import { ChangedPill } from "./display-helpers"

export interface CurrencyItem {
  code: string
  text: string
}

export interface NumericFieldProps {
  fieldId?: string
  label: string
  value: string
  onChange: (value: string) => void
  error?: string
  currencyError?: string
  required?: boolean
  atsHint?: React.ReactNode
  changedPill?: boolean
  currencyCode?: string | null
  onCurrencyChange?: (code: string) => void
  currencyItems?: CurrencyItem[]
  disabled?: boolean
  warning?: string
}

export function NumericField({
  fieldId,
  label,
  value,
  onChange,
  error,
  currencyError,
  required,
  atsHint,
  changedPill,
  currencyCode,
  onCurrencyChange,
  currencyItems,
  disabled,
  warning,
}: NumericFieldProps) {
  const generatedId = useId()
  const resolvedFieldId = fieldId ?? `numeric-field-${generatedId}`
  const errorId = `${resolvedFieldId}-error`
  const hasCurrencyDropdown = currencyItems && currencyItems.length > 0 && onCurrencyChange
  const hasAnyError = error || currencyError

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <Label htmlFor={resolvedFieldId} className="text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </Label>
        {atsHint}
        {changedPill && <ChangedPill />}
      </div>
      <div className={cn(
        "flex items-center rounded-md border border-input bg-background",
        disabled && "opacity-50 cursor-not-allowed",
      )}>
        {hasCurrencyDropdown && (
          <div className="shrink-0 bg-muted border-r border-input rounded-l-md">
            <Select
              value={currencyCode ?? ""}
              onValueChange={onCurrencyChange}
              disabled={disabled}
            >
              <SelectTrigger
                className={cn(
                  "h-9 w-auto min-w-[4.5rem] px-2 text-sm text-muted-foreground border-0 shadow-none bg-transparent rounded-l-md rounded-r-none",
                  "focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ring focus:ring-offset-0",
                  currencyError && "ring-1 ring-inset ring-red-400",
                )}
                aria-invalid={!!currencyError}
                aria-describedby={currencyError ? errorId : undefined}
              >
                <SelectValue placeholder="--" />
              </SelectTrigger>
              <SelectContent>
                {currencyItems.map((item) => (
                  <SelectItem key={item.code} value={item.code}>
                    {item.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <input
          id={resolvedFieldId}
          type="text"
          inputMode="decimal"
          value={value}
          aria-invalid={!!error}
          aria-describedby={hasAnyError || warning ? errorId : undefined}
          readOnly={disabled}
          onChange={(e) => {
            const v = e.target.value
              .replace(/[^0-9.]/g, "")
              .replace(/(\..*)\./g, "$1")
            onChange(v)
          }}
          className={cn(
            "flex-1 h-9 px-3 text-sm text-right tabular-nums bg-transparent border-0 min-w-0 placeholder:text-muted-foreground",
            "outline-none focus:ring-2 focus:ring-inset focus:ring-ring focus:ring-offset-0",
            error && "ring-1 ring-inset ring-red-400",
            hasCurrencyDropdown ? "rounded-r-md" : "rounded-md",
            disabled && "cursor-not-allowed",
          )}
        />
      </div>
      <p id={errorId} className={cn("text-xs min-h-4 mt-0.5", hasAnyError ? "text-red-500" : "text-amber-600")}>
        {error && currencyError ? "Currency and rate are required." : error ?? currencyError ?? warning}
      </p>
    </div>
  )
}
